<script setup>
import { computed, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import GateCard from 'dashboard/components-next/captain/pageComponents/safety-gates/GateCard.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const assistantId = computed(() => Number(route.params.assistantId));
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);

const backUrl = computed(() => ({
  name: 'captain_assistants_settings_index',
  params: {
    accountId: route.params.accountId,
    assistantId: assistantId.value,
  },
}));

const DEFAULTS = {
  crisis_detection_enabled: true,
  response_verification_enabled: true,
  financial_safety_enabled: true,
  max_single_refund: 200,
  max_daily_refund: 300,
  refund_warn_threshold: 100,
  pii_detection_enabled: true,
  pii_detection_action: 'log',
  internal_sanitization_enabled: true,
  reasoning_leak_sanitizer_enabled: true,
  retention_loop_enabled: true,
  max_retention_offers: 1,
  lazy_escalation_enabled: true,
  max_lazy_escalation_retries: 3,
  closing_flow_enabled: true,
  closing_flow_lookback: 5,
  closing_flow_min_repeats: 2,
  chat_signature_stripper_enabled: true,
  handoff_suppression_enabled: true,
  handoff_value_threshold: 300,
  max_handoff_retries: 2,
  error_circuit_breaker_enabled: true,
  max_error_messages: 1,
  error_window_hours: 4,
  auto_resolve_enabled: true,
  auto_resolve_delay_minutes: 5,
  prompt_caching_enabled: true,
};

const safetyGates = [
  {
    key: 'crisis_detection',
    section: 'safety',
    severity: 'critical',
    fields: [],
  },
  {
    key: 'response_verification',
    section: 'safety',
    severity: 'critical',
    fields: [],
  },
  {
    key: 'financial_safety',
    section: 'safety',
    severity: 'critical',
    fields: [
      {
        key: 'max_single_refund',
        type: 'number',
        min: 10,
        max: 10000,
        suffix: '$',
      },
      {
        key: 'max_daily_refund',
        type: 'number',
        min: 10,
        max: 10000,
        suffix: '$',
      },
      {
        key: 'refund_warn_threshold',
        type: 'number',
        min: 10,
        max: 5000,
        suffix: '$',
      },
    ],
  },
  {
    key: 'pii_detection',
    section: 'safety',
    severity: 'warning',
    fields: [
      {
        key: 'pii_detection_action',
        type: 'select',
        options: ['log', 'strip'],
      },
    ],
  },
  {
    key: 'internal_sanitization',
    section: 'safety',
    severity: 'warning',
    fields: [],
  },
  {
    key: 'reasoning_leak_sanitizer',
    section: 'safety',
    severity: 'warning',
    fields: [],
  },
  {
    key: 'retention_loop',
    section: 'quality',
    severity: 'warning',
    fields: [{ key: 'max_retention_offers', type: 'number', min: 1, max: 5 }],
  },
  {
    key: 'lazy_escalation',
    section: 'quality',
    severity: 'warning',
    fields: [
      { key: 'max_lazy_escalation_retries', type: 'number', min: 1, max: 5 },
    ],
  },
  {
    key: 'closing_flow',
    section: 'quality',
    severity: 'info',
    fields: [
      { key: 'closing_flow_lookback', type: 'number', min: 1, max: 20 },
      { key: 'closing_flow_min_repeats', type: 'number', min: 1, max: 5 },
    ],
  },
  {
    key: 'chat_signature_stripper',
    section: 'quality',
    severity: 'info',
    fields: [],
  },
  {
    key: 'handoff_suppression',
    section: 'quality',
    severity: 'warning',
    fields: [
      {
        key: 'handoff_value_threshold',
        type: 'number',
        min: 50,
        max: 5000,
        suffix: '$',
      },
      { key: 'max_handoff_retries', type: 'number', min: 1, max: 3 },
    ],
  },
  {
    key: 'error_circuit_breaker',
    section: 'quality',
    severity: 'info',
    fields: [
      { key: 'max_error_messages', type: 'number', min: 1, max: 5 },
      { key: 'error_window_hours', type: 'number', min: 1, max: 24 },
    ],
  },
  {
    key: 'auto_resolve',
    section: 'performance',
    severity: 'info',
    fields: [
      { key: 'auto_resolve_delay_minutes', type: 'number', min: 1, max: 30 },
    ],
  },
  {
    key: 'prompt_caching',
    section: 'performance',
    severity: 'info',
    fields: [],
  },
];

const sections = [
  { key: 'safety', gates: safetyGates.filter(g => g.section === 'safety') },
  { key: 'quality', gates: safetyGates.filter(g => g.section === 'quality') },
  {
    key: 'performance',
    gates: safetyGates.filter(g => g.section === 'performance'),
  },
];

const buildInitialState = () => {
  const state = {};
  safetyGates.forEach(gate => {
    state[`${gate.key}_enabled`] = DEFAULTS[`${gate.key}_enabled`];
    gate.fields.forEach(field => {
      state[field.key] = DEFAULTS[field.key];
    });
  });
  return state;
};

const localState = reactive(buildInitialState());

const getGateValue = key => {
  const config = assistant.value?.config;
  const val = config?.[key];
  return val !== undefined && val !== null ? val : DEFAULTS[key];
};

const initLocalState = () => {
  safetyGates.forEach(gate => {
    const enabledKey = `${gate.key}_enabled`;
    localState[enabledKey] = getGateValue(enabledKey);
    gate.fields.forEach(field => {
      localState[field.key] = getGateValue(field.key);
    });
  });
};

watch(
  () => assistant.value,
  () => {
    if (assistant.value) {
      initLocalState();
    }
  },
  { immediate: true }
);

const handleToggle = (gateKey, newValue) => {
  localState[`${gateKey}_enabled`] = newValue;
};

const handleFieldUpdate = (fieldKey, newValue) => {
  localState[fieldKey] = newValue;
};

const isSaving = computed(() => uiFlags.value.updatingItem);

const handleSave = async () => {
  try {
    const configUpdates = {};
    safetyGates.forEach(gate => {
      const enabledKey = `${gate.key}_enabled`;
      configUpdates[enabledKey] = localState[enabledKey];
      gate.fields.forEach(field => {
        configUpdates[field.key] = localState[field.key];
      });
    });

    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      assistant: {
        config: { ...assistant.value.config, ...configUpdates },
      },
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SAFETY_GATES.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CAPTAIN.ASSISTANTS.SAFETY_GATES.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const getFieldValues = gate => {
  const values = {};
  gate.fields.forEach(field => {
    values[field.key] = localState[field.key];
  });
  return values;
};
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.ASSISTANTS.SAFETY_GATES.TITLE')"
    :is-fetching="isFetching"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        :heading="t('CAPTAIN.ASSISTANTS.SAFETY_GATES.TITLE')"
        :description="t('CAPTAIN.ASSISTANTS.SAFETY_GATES.DESCRIPTION')"
      />

      <div class="flex flex-col gap-8 mt-7">
        <div
          v-for="section in sections"
          :key="section.key"
          class="flex flex-col gap-4"
        >
          <SettingsHeader
            :heading="
              t(
                `CAPTAIN.ASSISTANTS.SAFETY_GATES.SECTIONS.${section.key.toUpperCase()}.TITLE`
              )
            "
            :description="
              t(
                `CAPTAIN.ASSISTANTS.SAFETY_GATES.SECTIONS.${section.key.toUpperCase()}.DESCRIPTION`
              )
            "
          />
          <div class="flex flex-col gap-3">
            <GateCard
              v-for="gate in section.gates"
              :key="gate.key"
              :gate-key="gate.key"
              :enabled="localState[`${gate.key}_enabled`] ?? true"
              :severity="gate.severity"
              :fields="gate.fields"
              :values="getFieldValues(gate)"
              @toggle="value => handleToggle(gate.key, value)"
              @update-field="
                (fieldKey, value) => handleFieldUpdate(fieldKey, value)
              "
            />
          </div>
        </div>

        <div class="flex justify-end pt-4 pb-8 border-t border-n-weak">
          <Button
            :label="t('CAPTAIN.ASSISTANTS.SAFETY_GATES.SAVE_BUTTON')"
            :is-loading="isSaving"
            @click="handleSave"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
